/**
 * Shared layout wrapper for single-product partner sections (CRM or Marketing).
 * Provides the dark gradient background, centered header block, and "Built for scale" footer.
 * Does not handle the "Both" case — that uses PlatformDemoSection directly.
 */
import type { ReactNode } from "react";

interface ProductSectionLayoutProps {
  badge: ReactNode;
  heading: ReactNode;
  description: ReactNode;
  footerText?: string;
  children: ReactNode;
}

export default function ProductSectionLayout({
  badge,
  heading,
  description,
  footerText = "Built for scale.",
  children,
}: ProductSectionLayoutProps) {
  return (
    <section className="w-full bg-gradient-to-t from-[#535353] from-[8%] to-black overflow-hidden">
      <div className="flex flex-col items-center gap-6 px-5 md:px-16 lg:px-28 pt-16 md:pt-24 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex flex-col items-center gap-6 text-center max-w-4xl">
          {badge}
          <h2 className="font-clash font-medium text-white text-4xl md:text-5xl lg:text-6xl leading-none tracking-tight">
            {heading}
          </h2>
          <p className="text-ash-300 text-sm md:text-base leading-relaxed">{description}</p>
          <p className="text-clay-500 text-sm">{footerText}</p>
        </div>

        {/* Content (feature grid, stat cards, screenshot, etc.) */}
        {children}
      </div>
    </section>
  );
}
